import type { ContextType } from '@mindtype/shared'

interface Segment {
  context: ContextType
  name: string
  startIndex: number
  endIndex: number
}

interface SegmentedProgressProps {
  currentIndex: number
  totalQuestions: number
  contextBreakpoints: Map<number, ContextType>
}

const CONTEXT_NAMES: Record<ContextType, string> = {
  natural: '日常',
  work: '工作',
  stress: '压力',
  mixed: '综合',
}

export function SegmentedProgress({ currentIndex, totalQuestions, contextBreakpoints }: SegmentedProgressProps) {
  // 构建段落信息
  const segments: Segment[] = []
  const breakpointEntries = Array.from(contextBreakpoints.entries()).sort((a, b) => a[0] - b[0])

  for (let i = 0; i < breakpointEntries.length; i++) {
    const [startIndex, context] = breakpointEntries[i]
    const endIndex = i < breakpointEntries.length - 1 ? breakpointEntries[i + 1][0] : totalQuestions

    segments.push({
      context,
      name: CONTEXT_NAMES[context],
      startIndex,
      endIndex,
    })
  }

  // 确定当前段落
  const currentSegmentIndex = segments.findIndex(
    (seg) => currentIndex >= seg.startIndex && currentIndex < seg.endIndex
  )

  return (
    <div className="space-y-2">
      {/* 段落标签 - 使用与进度条相同的 flex 比例 */}
      <div className="flex gap-1">
        {segments.map((segment, index) => {
          const segmentLength = segment.endIndex - segment.startIndex
          const isActive = index === currentSegmentIndex
          const isCompleted = currentIndex >= segment.endIndex

          return (
            <div
              key={segment.context}
              className="flex flex-col items-center"
              style={{ flex: segmentLength }}
            >
              {/* 段落名称 */}
              <span
                className={`text-xs font-medium mb-1.5 ${
                  isActive
                    ? 'text-blue-600'
                    : isCompleted
                    ? 'text-blue-500'
                    : 'text-gray-400'
                }`}
              >
                {segment.name}
              </span>
              {/* 状态指示器 */}
              <div
                className={`w-2.5 h-2.5 rounded-full ${
                  isActive
                    ? 'bg-blue-600'
                    : isCompleted
                    ? 'bg-blue-400'
                    : 'bg-gray-300 border border-gray-300'
                }`}
              />
            </div>
          )
        })}
      </div>

      {/* 分段进度条 */}
      <div className="flex gap-1">
        {segments.map((segment, index) => {
          const segmentLength = segment.endIndex - segment.startIndex
          const isActive = index === currentSegmentIndex
          const isCompleted = currentIndex >= segment.endIndex

          // 计算当前段的进度
          let segmentProgress = 0
          if (isCompleted) {
            segmentProgress = 100
          } else if (isActive) {
            const progressInSegment = currentIndex - segment.startIndex + 1
            segmentProgress = (progressInSegment / segmentLength) * 100
          }

          return (
            <div
              key={segment.context}
              className="h-1.5 bg-blue-100 rounded-full overflow-hidden"
              style={{ flex: segmentLength }}
            >
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  isActive ? 'bg-blue-500' : isCompleted ? 'bg-blue-400' : 'bg-blue-100'
                }`}
                style={{ width: `${segmentProgress}%` }}
              />
            </div>
          )
        })}
      </div>

      {/* 当前进度文字 */}
      <div className="flex justify-between items-center text-xs text-gray-500 pt-1">
        <span>
          <span className="text-blue-600 font-medium">{segments[currentSegmentIndex]?.name || ''}</span>
          {' '}
          {currentSegmentIndex >= 0 && (
            <span className="text-gray-400">
              ({currentIndex - (segments[currentSegmentIndex]?.startIndex || 0) + 1}/
              {(segments[currentSegmentIndex]?.endIndex || 0) - (segments[currentSegmentIndex]?.startIndex || 0)})
            </span>
          )}
        </span>
        <span>
          {currentIndex + 1} / {totalQuestions}
        </span>
      </div>
    </div>
  )
}
